import { pad3, sortNumericAsc } from "@tedivo/tedivo-pure-helpers";
import { BayLevelEnum } from "open-vessel-definition";
export const DECK_TIERS_MODES = ["none", "below", "above", "both"];
/**
 * The number of tiers stacked in every occupied stack of the deck view, one
 * entry per bay group and row.
 *
 * Bay groups follow the top bay numbers (createSvgTopBayNumbers): a bay with a
 * `confirmedPairedBay` and its partner make one group, so a 40' pair is one
 * group of stacks, not two. That pairing is decided over both
 * levels, whereas the smart blocks are built per level, so the blocks are looked
 * up per level rather than grouped.
 *
 * The count per stack is its occupied tiers at the level(s) of the mode (two
 * 20s side by side in a tier count as one). A stack with cargo at the other
 * level only is still listed, with 0, so its symbol shows the stack is empty
 * for what is being counted. Stacks with no cargo at all are left out.
 */
export function countDeckTiersByStack(smartCGs, lcgsBy20Bay, mode) {
    const countedLevels = mode === "below"
        ? [BayLevelEnum.BELOW]
        : mode === "above"
            ? [BayLevelEnum.ABOVE]
            : [BayLevelEnum.BELOW, BayLevelEnum.ABOVE];
    const allBays = Object.keys(lcgsBy20Bay).sort(sortNumericAsc);
    const handled = new Set();
    const stacks = [];
    allBays.forEach((bay) => {
        var _a, _b, _c;
        if (handled.has(bay))
            return;
        const lcgsOfBay = lcgsBy20Bay[bay];
        const lcg = (_a = lcgsOfBay.lcgs.get(20)) === null || _a === void 0 ? void 0 : _a.lcg;
        if (lcg === undefined)
            return;
        handled.add(bay);
        const pairedBay = lcgsOfBay.confirmedPairedBay;
        const pairedLcg = pairedBay ? (_c = (_b = lcgsBy20Bay[pairedBay]) === null || _b === void 0 ? void 0 : _b.lcgs.get(20)) === null || _c === void 0 ? void 0 : _c.lcg : undefined;
        const bays = [bay];
        const lookupBays = [bay];
        let groupLcg = lcg;
        if (pairedBay && pairedLcg !== undefined) {
            handled.add(pairedBay);
            bays.push(pairedBay);
            bays.sort(sortNumericAsc);
            // The paired block is registered under the even bay as well
            const evenBay = pad3(Math.round((Number(bay) + Number(pairedBay)) / 2));
            lookupBays.push(evenBay, pairedBay);
            groupLcg = (lcg + pairedLcg) / 2;
        }
        // Per row: tiers at each level, and the row's TCG from any container in it
        const rows = new Map();
        [BayLevelEnum.BELOW, BayLevelEnum.ABOVE].forEach((level) => {
            blocksOf(smartCGs, lookupBays, level).forEach((block) => {
                block.getSmartStacksFromPortToStarboard().forEach((stack) => {
                    var _a;
                    const tiers = stack.getTiersFromBottomTierToTop();
                    if (tiers.length === 0)
                        return;
                    const row = stack.stack;
                    const entry = (_a = rows.get(row)) !== null && _a !== void 0 ? _a : {
                        tcg: undefined,
                        tiers: { [BayLevelEnum.BELOW]: 0, [BayLevelEnum.ABOVE]: 0 },
                    };
                    entry.tiers[level] = Math.max(entry.tiers[level], tiers.length);
                    if (entry.tcg === undefined)
                        entry.tcg = tcgOf(tiers);
                    rows.set(row, entry);
                });
            });
        });
        rows.forEach((entry, row) => {
            if (entry.tcg === undefined)
                return;
            stacks.push({
                bays,
                row,
                lcg: groupLcg,
                tcg: entry.tcg,
                count: countedLevels.reduce((sum, level) => sum + entry.tiers[level], 0),
            });
        });
    });
    return stacks;
}
/** The distinct blocks of a level that hold the given bays */
function blocksOf(smartCGs, bays, level) {
    const blocks = new Set();
    bays.forEach((bay) => {
        var _a;
        const block = (_a = smartCGs.smartBayBlocksByBay[bay]) === null || _a === void 0 ? void 0 : _a[level];
        if (block)
            blocks.add(block);
    });
    return Array.from(blocks);
}
/** The TCG of the first container found in the stack's tiers */
function tcgOf(tiers) {
    var _a;
    for (const tier of tiers) {
        for (const container of tier.containers) {
            // A container without CGs is reported elsewhere; it has no position here
            const tcg = "cgs" in container ? (_a = container.cgs) === null || _a === void 0 ? void 0 : _a.tcg : undefined;
            if (tcg !== undefined)
                return tcg;
        }
    }
    return undefined;
}
/**
 * The counts keyed `"${oddBay}-${row}"`, one key per odd bay of the group, so a
 * container can look its stack up by its own odd bay (`isoOddBay`) and row.
 */
export function tiersCountByStackKey(stacks) {
    const byKey = new Map();
    stacks.forEach((stack) => {
        stack.bays.forEach((bay) => byKey.set(`${bay}-${stack.row}`, stack.count));
    });
    return byKey;
}
/** The tallest stack on board, at least 1 so a ramp over it never divides by zero */
export function maxTiersCount(stacks) {
    return stacks.reduce((max, stack) => Math.max(max, stack.count), 1);
}
/**
 * The top of the shading ramp: the taller of the tallest hold stack and the
 * tallest deck stack on board, at least 1. One scale for both levels, so the
 * hold and the deck of the same vessel are comparable, and never the hold+deck
 * sum, which no single level reaches.
 */
export function deckTiersRampMax(smartCGs, lcgsBy20Bay) {
    return Math.max(maxTiersCount(countDeckTiersByStack(smartCGs, lcgsBy20Bay, "below")), maxTiersCount(countDeckTiersByStack(smartCGs, lcgsBy20Bay, "above")));
}
